<template>
    <Institutelayout>
    <div class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div class="container mx-auto max-w-6xl">
        
        <!-- Form Section - Full Width -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">Principle Configuration</h2>
          </div>
          
          <div class="p-6">
            <form @submit.prevent="saveChanges" class="space-y-6">
              <!-- Principle and Vice Principle Section - Side by Side -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Principle Section -->
                <div class="bg-gray-50 rounded-xl p-4">
                  <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    Principle Profile
                  </h3>
                  
                  <div class="space-y-4">
                    <!-- Principle Image -->
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 mb-2">
                        Principle Image
                      </label>
                      <div class="relative">
                        <input 
                          type="file" 
                          id="principleImage" 
                          @change="handlePrincipleImageUpload" 
                          accept="image/*"
                          class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors duration-200 bg-white">
                          <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                          </svg>
                          <p class="text-gray-600 font-medium text-sm">
                            {{ form.principle.imageName || 'Click to upload image' }}
                          </p>
                          <p class="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                        </div>
                      </div>
                    </div>
  
                    <!-- Principle Title -->
                    <div>
                      <label for="principleTitle" class="block text-sm font-semibold text-gray-700 mb-2">
                        Title <span class="text-red-500">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="principleTitle" 
                        v-model="form.principle.title" 
                        required 
                        class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                        placeholder="e.g., Dr. John Smith - Principal"
                      />
                    </div>
  
                    <!-- Principle Description -->
                    <div>
                      <label for="principleDescription" class="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea 
                        id="principleDescription" 
                        v-model="form.principle.description" 
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm resize-none"
                        placeholder="Enter principle description"
                      ></textarea>
                    </div>
  
                    <!-- Principle CTA Buttons -->
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 mb-2">
                        Call to Action Buttons
                      </label>
                      <div class="space-y-2 max-h-32 overflow-y-auto">
                        <div v-for="(button, index) in form.principle.ctaButtons" :key="index" class="flex items-center space-x-2 bg-white p-2 rounded border border-gray-200">
                          <input 
                            type="text" 
                            v-model="button.text" 
                            placeholder="Button Text" 
                            class="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                          />
                          <input 
                            type="url" 
                            v-model="button.url" 
                            placeholder="URL" 
                            class="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                          />
                          <input 
                            type="color" 
                            v-model="button.color" 
                            class="w-6 h-6 border border-gray-200 rounded cursor-pointer"
                          />
                          <button 
                            type="button" 
                            @click="removePrincipleCtaButton(index)" 
                            class="p-1 text-red-500 hover:text-red-700 rounded"
                          >
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                          </button>
                        </div>
                        <button 
                          type="button" 
                          @click="addPrincipleCtaButton" 
                          class="w-full py-1.5 px-2 border border-dashed border-gray-300 rounded text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center space-x-1 text-xs"
                        >
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                          </svg>
                          <span>Add CTA Button</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
  
                <!-- Vice Principle Section -->
                <div class="bg-gray-50 rounded-xl p-4">
                  <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    Vice Principle Profile
                  </h3>
                  
                  <div class="space-y-4">
                    <!-- Vice Principle Image -->
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 mb-2">
                        Vice Principle Image
                      </label>
                      <div class="relative">
                        <input 
                          type="file" 
                          id="vicePrincipleImage" 
                          @change="handleVicePrincipleImageUpload" 
                          accept="image/*"
                          class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors duration-200 bg-white">
                          <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                          </svg>
                          <p class="text-gray-600 font-medium text-sm">
                            {{ form.vicePrinciple.imageName || 'Click to upload image' }}
                          </p>
                          <p class="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                        </div>
                      </div>
                    </div>
  
                    <!-- Vice Principle Title -->
                    <div>
                      <label for="vicePrincipleTitle" class="block text-sm font-semibold text-gray-700 mb-2">
                        Title <span class="text-red-500">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="vicePrincipleTitle" 
                        v-model="form.vicePrinciple.title" 
                        required 
                        class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                        placeholder="e.g., Mrs. Jane Doe - Vice Principal"
                      />
                    </div>
  
                    <!-- Vice Principle Description -->
                    <div>
                      <label for="vicePrincipleDescription" class="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea 
                        id="vicePrincipleDescription" 
                        v-model="form.vicePrinciple.description" 
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm resize-none"
                        placeholder="Enter vice principle description"
                      ></textarea>
                    </div>
  
                    <!-- Vice Principle CTA Buttons -->
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 mb-2">
                        Call to Action Buttons
                      </label>
                      <div class="space-y-2 max-h-32 overflow-y-auto">
                        <div v-for="(button, index) in form.vicePrinciple.ctaButtons" :key="index" class="flex items-center space-x-2 bg-white p-2 rounded border border-gray-200">
                          <input 
                            type="text" 
                            v-model="button.text" 
                            placeholder="Button Text" 
                            class="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                          />
                          <input 
                            type="url" 
                            v-model="button.url" 
                            placeholder="URL" 
                            class="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                          />
                          <input 
                            type="color" 
                            v-model="button.color" 
                            class="w-6 h-6 border border-gray-200 rounded cursor-pointer"
                          />
                          <button 
                            type="button" 
                            @click="removeVicePrincipleCtaButton(index)" 
                            class="p-1 text-red-500 hover:text-red-700 rounded"
                          >
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                          </button>
                        </div>
                        <button 
                          type="button" 
                          @click="addVicePrincipleCtaButton" 
                          class="w-full py-1.5 px-2 border border-dashed border-gray-300 rounded text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center space-x-1 text-xs"
                        >
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                          </svg>
                          <span>Add CTA Button</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <!-- Design Customization Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Design Customization
                </h3>
                
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label for="titleColor" class="block text-sm font-semibold text-gray-700 mb-2">
                      Title Color
                    </label>
                    <div class="flex items-center space-x-2">
                      <input 
                        type="color" 
                        id="titleColor" 
                        v-model="form.titleColor" 
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{ form.titleColor }}</span>
                    </div>
                  </div>
  
                  <div>
                    <label for="descriptionColor" class="block text-sm font-semibold text-gray-700 mb-2">
                      Description Color
                    </label>
                    <div class="flex items-center space-x-2">
                      <input 
                        type="color" 
                        id="descriptionColor" 
                        v-model="form.descriptionColor" 
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{ form.descriptionColor }}</span>
                    </div>
                  </div>
  
                  <div>
                    <label for="fontFamily" class="block text-sm font-semibold text-gray-700 mb-2">
                      Font Family
                    </label>
                    <select 
                      id="fontFamily" 
                      v-model="form.fontFamily" 
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                    >
                      <option value="sans-serif">Sans-serif</option>
                      <option value="serif">Serif</option>
                      <option value="monospace">Monospace</option>
                      <option value="cursive">Cursive</option>
                      <option value="fantasy">Fantasy</option>
                    </select>
                  </div>
  
                  <div>
                    <label for="titleSize" class="block text-sm font-semibold text-gray-700 mb-2">
                      Title Size
                    </label>
                    <select 
                      id="titleSize" 
                      v-model="form.titleSize" 
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                    >
                      <option value="text-lg">Small</option>
                      <option value="text-xl">Medium</option>
                      <option value="text-2xl">Large</option>
                      <option value="text-3xl">Extra Large</option>
                    </select>
                  </div>
  
                  <div>
                    <label for="descriptionSize" class="block text-sm font-semibold text-gray-700 mb-2">
                      Description Size
                    </label>
                    <select 
                      id="descriptionSize" 
                      v-model="form.descriptionSize" 
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                    >
                      <option value="text-sm">Small</option>
                      <option value="text-base">Medium</option>
                      <option value="text-lg">Large</option>
                    </select>
                  </div>
  
                  <div>
                    <label for="cardBgColor" class="block text-sm font-semibold text-gray-700 mb-2">
                      Card Background
                    </label>
                    <div class="flex items-center space-x-2">
                      <input 
                        type="color" 
                        id="cardBgColor" 
                        v-model="form.cardBgColor" 
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{ form.cardBgColor }}</span>
                    </div>
                  </div>
                </div>
              </div>
  
              <!-- Save Button -->
              <div class="flex justify-end pt-4 border-t border-gray-200">
                <button 
                  type="submit" 
                  class="px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span class="flex items-center space-x-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    <span>Save Changes</span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
  
        <!-- Preview Section - Below Form -->
        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 hidden md:flex flex-col overflow-hidden">
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">Live Preview</h2>
          </div>
          
          <div class="p-6">
            <!-- Preview Principle Profiles -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <!-- Principle Card -->
              <div 
                class="rounded-xl p-6 shadow-lg border border-gray-200"
                :style="{ 
                  backgroundColor: form.cardBgColor,
                  fontFamily: form.fontFamily 
                }"
              >
                <div class="text-center">
                  <!-- Principle Image -->
                  <div class="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    <img 
                      v-if="form.principle.imageUrl" 
                      :src="form.principle.imageUrl" 
                      alt="Principle" 
                      class="w-full h-full object-cover"
                    />
                    <svg v-else class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
  
                  <!-- Principle Title -->
                  <h3 
                    :class="form.titleSize"
                    class="font-bold mb-3"
                    :style="{ 
                      color: form.titleColor,
                      fontFamily: form.fontFamily
                    }"
                  >
                    {{ form.principle.title || 'Principal Name' }}
                  </h3>
  
                  <!-- Principle Description -->
                  <p 
                    v-if="form.principle.description"
                    :class="form.descriptionSize"
                    class="mb-4 leading-relaxed"
                    :style="{ 
                      color: form.descriptionColor,
                      fontFamily: form.fontFamily
                    }"
                  >
                    {{ form.principle.description }}
                  </p>
  
                  <!-- Principle CTA Buttons -->
                  <div 
                    v-if="form.principle.ctaButtons.length > 0"
                    class="flex flex-wrap gap-2 justify-center"
                  >
                    <button 
                      v-for="(button, index) in form.principle.ctaButtons" 
                      :key="index"
                      class="px-4 py-2 font-medium rounded-lg hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-sm"
                      :style="{ 
                        backgroundColor: button.color || '#3b82f6',
                        color: getContrastColor(button.color || '#3b82f6'),
                        fontFamily: form.fontFamily
                      }"
                    >
                      {{ button.text || `Button ${index + 1}` }}
                    </button>
                  </div>
                </div>
              </div>
  
              <!-- Vice Principle Card -->
              <div 
                class="rounded-xl p-6 shadow-lg border border-gray-200"
                :style="{ 
                  backgroundColor: form.cardBgColor,
                  fontFamily: form.fontFamily 
                }"
              >
                <div class="text-center">
                  <!-- Vice Principle Image -->
                  <div class="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    <img 
                      v-if="form.vicePrinciple.imageUrl" 
                      :src="form.vicePrinciple.imageUrl" 
                      alt="Vice Principle" 
                      class="w-full h-full object-cover"
                    />
                    <svg v-else class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
  
                  <!-- Vice Principle Title -->
                  <h3 
                    :class="form.titleSize"
                    class="font-bold mb-3"
                    :style="{ 
                      color: form.titleColor,
                      fontFamily: form.fontFamily
                    }"
                  >
                    {{ form.vicePrinciple.title || 'Vice Principal Name' }}
                  </h3>
  
                  <!-- Vice Principle Description -->
                  <p 
                    v-if="form.vicePrinciple.description"
                    :class="form.descriptionSize"
                    class="mb-4 leading-relaxed"
                    :style="{ 
                      color: form.descriptionColor,
                      fontFamily: form.fontFamily
                    }"
                  >
                    {{ form.vicePrinciple.description }}
                  </p>
  
                  <!-- Vice Principle CTA Buttons -->
                  <div 
                    v-if="form.vicePrinciple.ctaButtons.length > 0"
                    class="flex flex-wrap gap-2 justify-center"
                  >
                    <button 
                      v-for="(button, index) in form.vicePrinciple.ctaButtons" 
                      :key="index"
                      class="px-4 py-2 font-medium rounded-lg hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-sm"
                      :style="{ 
                        backgroundColor: button.color || '#3b82f6',
                        color: getContrastColor(button.color || '#3b82f6'),
                        fontFamily: form.fontFamily
                      }"
                    >
                      {{ button.text || `Button ${index + 1}` }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
  
            <!-- Action Buttons -->
            <div class="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
              <div class="text-sm text-gray-500">
                Preview updates in real-time as you make changes
              </div>
              <div class="flex space-x-3">
                <button 
                  @click="deletePrinciple"
                  class="px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors duration-200 flex items-center space-x-2"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</Institutelayout>
  </template>
  
  <script setup>
  import { ref } from 'vue';
import Institutelayout from '../institutelayout.vue';
  
  const form = ref({
    principle: {
      image: null,
      imageName: '',
      imageUrl: '',
      title: '',
      description: '',
      ctaButtons: [],
    },
    vicePrinciple: {
      image: null,
      imageName: '',
      imageUrl: '',
      title: '',
      description: '',
      ctaButtons: [],
    },
    titleColor: '#1f2937',
    descriptionColor: '#6b7280',
    fontFamily: 'sans-serif',
    titleSize: 'text-xl',
    descriptionSize: 'text-base',
    cardBgColor: '#ffffff',
  });
  
  const handlePrincipleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      form.value.principle.image = file;
      form.value.principle.imageName = file.name;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        form.value.principle.imageUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      form.value.principle.image = null;
      form.value.principle.imageName = '';
      form.value.principle.imageUrl = '';
    }
  };
  
  const handleVicePrincipleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      form.value.vicePrinciple.image = file;
      form.value.vicePrinciple.imageName = file.name;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        form.value.vicePrinciple.imageUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      form.value.vicePrinciple.image = null;
      form.value.vicePrinciple.imageName = '';
      form.value.vicePrinciple.imageUrl = '';
    }
  };
  
  const addPrincipleCtaButton = () => {
    form.value.principle.ctaButtons.push({ 
      text: '', 
      url: '', 
      color: '#3b82f6'
    });
  };
  
  const removePrincipleCtaButton = (index) => {
    form.value.principle.ctaButtons.splice(index, 1);
  };
  
  const addVicePrincipleCtaButton = () => {
    form.value.vicePrinciple.ctaButtons.push({ 
      text: '', 
      url: '', 
      color: '#3b82f6'
    });
  };
  
  const removeVicePrincipleCtaButton = (index) => {
    form.value.vicePrinciple.ctaButtons.splice(index, 1);
  };
  
  const getContrastColor = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };
  
  const saveChanges = () => {
    console.log('Principle Data:', {
      ...form.value,
      principle: {
        ...form.value.principle,
        image: form.value.principle.image ? form.value.principle.image.name : null,
      },
      vicePrinciple: {
        ...form.value.vicePrinciple,
        image: form.value.vicePrinciple.image ? form.value.vicePrinciple.image.name : null,
      },
    });
    alert('Changes saved successfully! Check console for the submitted data.');
  };
  
  const editPrinciple = () => {
    alert('Edit functionality - This would allow you to modify the current principle configuration.');
  };
  
  const deletePrinciple = () => {
    if (confirm('Are you sure you want to delete this principle configuration? This action cannot be undone.')) {
      form.value = {
        principle: {
          image: null,
          imageName: '',
          imageUrl: '',
          title: '',
          description: '',
          ctaButtons: [],
        },
        vicePrinciple: {
          image: null,
          imageName: '',
          imageUrl: '',
          title: '',
          description: '',
          ctaButtons: [],
        },
        titleColor: '#1f2937',
        descriptionColor: '#6b7280',
        fontFamily: 'sans-serif',
        titleSize: 'text-xl',
        descriptionSize: 'text-base',
        cardBgColor: '#ffffff',
      };
      alert('Principle configuration deleted successfully!');
    }
  };
  </script>